import { Icon, YesObject } from '@yes.technology/react-toolkit'
import { useTranslation } from 'react-i18n-lite'
import DownloaderLoading from 'report/components/DownloaderLoading'
import useExportPDF from 'report/hooks/useExportPDF/useExportPDF'
import { FieldSettings } from 'shared/types'
import { ConcatFields } from 'shared/utils/object/concatenateFields'
import { RelationConfigProps } from '../types/object-view'

export type ObjectViewReportsProps = {
  isFetching: boolean
  yesObject?: YesObject
  filterQuery?: object
  informational?: boolean
  relationConfig: RelationConfigProps
  fieldSettings?: FieldSettings
  concatFields?: ConcatFields
  classificationUuid?: string
  isShowReports?: boolean
}

export default function ObjectViewReports({
  isFetching,
  yesObject,
  filterQuery,
  informational = false,
  relationConfig,
  fieldSettings,
  concatFields,
  classificationUuid,
  isShowReports = true
}: ObjectViewReportsProps) {
  const {
    exportPDFReport: exportSinglePDFReport,
    isLoading: isLoadingSinglePDFReport,
    batchProgress: batchProgressSinglePDFReport,
    populateObjectRelationsProgress:
      populateObjectRelationsProgressSinglePDFReport,
    renderProgress: renderProgressSinglePDFReport
  } = useExportPDF({
    reportType: 'card',
    downloadType: 'single-pdf',
    splitThreshold: 1,
    yesObject,
    relationConfig,
    fieldSettings,
    concatFields,
    classificationUuid
  })

  const {
    exportPDFReport: exportBatchPDFReport,
    isLoading: isLoadingBatchPDFReport,
    batchProgress: batchProgressBatchPDFReport,
    populateObjectRelationsProgress:
      populateObjectRelationsProgressBatchPDFReport,
    renderProgress: renderProgressBatchPDFReport
  } = useExportPDF({
    reportType: 'card',
    downloadType: 'multiple-pdf-zip',
    splitThreshold: 1,
    relationConfig,
    fieldSettings,
    concatFields,
    classificationUuid
  })

  const {
    exportPDFReport: exportMergedPDFReport,
    isLoading: isLoadingMergedPDFReport,
    batchProgress: batchProgressMergedPDFReport,
    populateObjectRelationsProgress:
      populateObjectRelationsProgressMergedPDFReport,
    renderProgress: renderProgressMergedPDFReport
  } = useExportPDF({
    reportType: 'card',
    downloadType: 'single-pdf',
    relationConfig: relationConfig,
    fieldSettings: fieldSettings,
    concatFields: concatFields,
    classificationUuid: classificationUuid
  })

  const { t } = useTranslation()

  const isLoadingReport =
    isLoadingSinglePDFReport ||
    isLoadingBatchPDFReport ||
    isLoadingMergedPDFReport

  const buttonClasses = 'btn btn-link p-0'

  if (isFetching || !filterQuery || !isShowReports) {
    return null
  }

  const getProgressMessage = (
    batchProgress: { currentBatch: number; isFetching: boolean },
    populateProgress: { currentBatch: number; isFetching: boolean },
    renderProgress: {
      isRendering: boolean
      currentPdf: number
      totalPdfs: number
    }
  ) => {
    if (batchProgress.isFetching) {
      return t('report.downloader-loading.batch-progress', {
        currentBatch: `${batchProgress.currentBatch}`
      })
    }

    if (populateProgress.isFetching) {
      return t('report.downloader-loading.populate-relation-progress', {
        currentBatch: `${populateProgress.currentBatch}`
      })
    }

    if (renderProgress.isRendering) {
      return t('report.downloader-loading.render-progress', {
        renderProgress: `${renderProgress.currentPdf}/${renderProgress.totalPdfs}`
      })
    }

    return ''
  }

  if (isLoadingReport) {
    const message =
      getProgressMessage(
        batchProgressSinglePDFReport,
        populateObjectRelationsProgressSinglePDFReport,
        renderProgressSinglePDFReport
      ) ||
      getProgressMessage(
        batchProgressBatchPDFReport,
        populateObjectRelationsProgressBatchPDFReport,
        renderProgressBatchPDFReport
      ) ||
      getProgressMessage(
        batchProgressMergedPDFReport,
        populateObjectRelationsProgressMergedPDFReport,
        renderProgressMergedPDFReport
      )

    return <DownloaderLoading customMessage={message} />
  }

  return (
    <div className='d-flex gap-3 p-1'>
      <button
        title={t('object-view.download-single-report')}
        className={buttonClasses}
        onClick={exportSinglePDFReport}
      >
        <Icon iconName='PDFFile' icSize='large' icColor='white' />
      </button>

      <button
        title={t('object-view.download-batch-report')}
        className={buttonClasses}
        onClick={exportBatchPDFReport}
      >
        <Icon iconName='PDFFileBatch' icSize='large' icColor='white' />
      </button>

      <button
        title={t('object-view.download-merged-report')}
        className={buttonClasses}
        onClick={exportMergedPDFReport}
      >
        <Icon iconName='PDFFileCompiled' icSize='large' icColor='white' />
      </button>
    </div>
  )
}
